import StorefrontIcon from 'public/icons/icn-storefront-outline.svg';
import { useTranslation } from 'react-i18next';
import ImageUrlResizer from 'utils/image-url-resizer';

import { ReviewedRole, ReviewedRoleType, ShopFeedbackItem as ShopFeedbackItemType } from '../../types/shop-feedback.types';

interface ShopFeedbackItemProps {
  item?: ShopFeedbackItemType | null;
  /** Role of the reviewed shop owner in the order. */
  role?: ReviewedRoleType | null;
  /** Shop name of who wrote the review ("Purchased by Johanna Donet:"). */
  reviewerName?: string | null;
  /** Name of the shop whose reviews these are, used on automatic reviews. */
  shopName?: string | null;
  /** Written by Yaga for the buyer: the box then names the shop, never the buyer (privacy). */
  automatic?: boolean;
}

/**
 * Box under a review that says what the review is about: the listing thumbnail, who sold or
 * bought it ("Purchased by Johanna Donet:"), and its category.
 * User reviews name the reviewer. Automatic reviews name the reviewed shop instead, because the
 * buyer never wrote them and their name should not appear (privacy).
 */
export const ShopFeedbackItem = ({ item, role, reviewerName, shopName, automatic }: ShopFeedbackItemProps) => {
  const { t } = useTranslation();

  if (!item) {
    return null;
  }

  // What the shop did in the order, and the reviewer did the opposite.
  const shopSide = role === ReviewedRole.SELLER ? 'sold' : role === ReviewedRole.BUYER ? 'purchased' : null;
  const reviewerSide = shopSide === 'sold' ? 'purchased' : shopSide === 'purchased' ? 'sold' : null;
  const side = automatic ? shopSide : reviewerSide;
  const name = automatic ? shopName : reviewerName;
  // Without a name (e.g. a deleted account) there is no label, only the photo and the category.
  const fullLabel = side && name ? t(`components.feedback.item.${side}-by`, { name }) : null;
  // The colon introduces the category, so it goes when there is no category line.
  const label = fullLabel && !item.categoryTitle ? fullLabel.replace(/\s*:\s*$/, '') : fullLabel;

  if (!label && !item.categoryTitle) {
    return null;
  }

  return (
    <div className="shop-feedback-item">
      {item.thumbnail ? (
        <img className="shop-feedback-item-image" src={ImageUrlResizer.resize(item.thumbnail, 100)} alt="" />
      ) : (
        <div className="shop-feedback-item-image shop-feedback-item-icon">
          <StorefrontIcon />
        </div>
      )}

      <div className="shop-feedback-item-text">
        {label && <div className="shop-feedback-item-label">{label}</div>}
        {item.categoryTitle && <div className="shop-feedback-item-category">{item.categoryTitle}</div>}
      </div>
    </div>
  );
};
